import { getTableColumns, is, SQL, sql } from "drizzle-orm";
import {
  type AnyPgColumn,
  type AnyPgInsert,
  makePgArray,
  PgArray,
  type PgDatabase,
  PgEnumColumn,
  type PgInsertDynamic,
  type PgQueryResultHKT,
  type PgTable,
} from "drizzle-orm/pg-core";
import type { SelectedFieldsFlat } from "drizzle-orm/pg-core/query-builders/select.types";
import type { SelectResultFields } from "drizzle-orm/query-builders/select.types";
import { prettyPrintSQL } from "../prettyPrintSQL";
import { type Logger, NOOP_LOGGER } from "./Logger";

type ConflictDoNothing = {
  type: "doNothing";
  target?: AnyPgColumn[];
};

type ConflictDoUpdate = {
  type: "doUpdate";
  target: AnyPgColumn[];
  set: Record<string, SQL>;
};

type ConflictStrategy = ConflictDoNothing | ConflictDoUpdate;

export type BulkInsertOptions<TSchema extends Record<string, unknown> = Record<string, unknown>> = {
  tx?: PgDatabase<PgQueryResultHKT, TSchema>;
  onConflict?: ConflictStrategy;
  returning?: SelectedFieldsFlat | true;
  chunkSize?: number;
};

/**
 * Bulk-insert rows using the PostgreSQL UNNEST technique.
 *
 * Each column is passed as a single typed array (`unnest($1::type[], $2::type[], …)`),
 * so the parameter count equals the number of columns regardless of row count.
 *
 * Columns not present in any row are filled from their Drizzle-defined defaults
 * (SQL expressions stay server-side; JS `$defaultFn` callbacks are evaluated per row).
 */
export class BulkInserter<TSchema extends Record<string, unknown> = Record<string, unknown>> {
  constructor(
    private readonly db: PgDatabase<PgQueryResultHKT, TSchema>,
    private readonly logger: Logger = NOOP_LOGGER,
  ) {}

  /**
   * Returns all columns, typed as `T['$inferSelect']`.
   */
  async insert<T extends PgTable>(
    table: T,
    rows: Record<string, unknown>[],
    options: BulkInsertOptions<TSchema> & { returning: true },
  ): Promise<T["$inferSelect"][]>;
  /**
   * Returns selected columns, typed via `SelectResultFields<TFields>`.
   */
  async insert<T extends PgTable, TFields extends SelectedFieldsFlat>(
    table: T,
    rows: Record<string, unknown>[],
    options: BulkInsertOptions<TSchema> & { returning: TFields },
  ): Promise<SelectResultFields<TFields>[]>;
  /**
   * Fire-and-forget insert with no return value.
   */
  async insert<T extends PgTable>(
    table: T,
    rows: Record<string, unknown>[],
    options?: BulkInsertOptions<TSchema>,
  ): Promise<void>;
  /**
   * Inserts multiple rows into the specified PostgreSQL table.
   *
   * When `options.returning` is set, the inserted rows are returned via a
   * SQL `RETURNING` clause. Pass `true` to return all columns (typed as
   * `T['$inferSelect']`), or a column-selection object to return specific
   * columns (typed via `SelectResultFields`). Rows that are skipped by an
   * `onConflict` strategy are excluded from the result.
   *
   * @param table   - The target PostgreSQL table.
   * @param rows    - Row objects to insert (column names as keys).
   * @param options - Conflict handling and returning behavior.
   * @returns The inserted rows when `returning` is set; nothing otherwise.
   */
  async insert<T extends PgTable>(
    table: T,
    rows: Record<string, unknown>[],
    options?: BulkInsertOptions<TSchema>,
  ): // biome-ignore lint/suspicious/noConfusingVoidType: void required for overload compatibility with Promise<void> signature
  Promise<T["$inferSelect"][] | Record<string, unknown>[] | void> {
    if (rows.length === 0) {
      if (options?.returning) return [];
      return;
    }

    const chunkSize = options?.chunkSize ?? 1000;
    const results: (T["$inferSelect"] | Record<string, unknown>)[] = [];

    for (let i = 0; i < rows.length; i += chunkSize) {
      const chunk = rows.slice(i, i + chunkSize);

      this.logger.debug(
        `[${this.constructor.name}] Inserting chunk ${i / chunkSize + 1}/${Math.ceil(rows.length / chunkSize)}`,
        {
          chunkNum: i / chunkSize + 1,
          chunkSize: chunk.length,
          numChunks: Math.ceil(rows.length / chunkSize),
        },
      );

      const chunkResult = await this.insertChunk(table, chunk, options);
      if (chunkResult !== undefined) {
        results.push(...chunkResult);
      }
    }

    if (options?.returning) return results;
  }

  private async insertChunk<T extends PgTable>(
    table: T,
    rows: Record<string, unknown>[],
    options?: BulkInsertOptions<TSchema>,
  ): Promise<(T["$inferSelect"] | Record<string, unknown>)[] | undefined> {
    const { unnestFragments, executeParams } = BulkInserter.buildColumns(getTableColumns(table), rows);
    const query = this.buildQuery(options?.tx ?? this.db, table, getTableColumns(table), unnestFragments, options);

    try {
      if (options?.returning === true) {
        return (await query.returning().execute(executeParams)) as T["$inferSelect"][];
      }

      if (options?.returning) {
        return (await query.returning(options.returning).execute(executeParams)) as Record<string, unknown>[];
      }

      await query.execute(executeParams);
      return undefined;
    } catch (err) {
      this.logger.error(
        `[${this.constructor.name}] Chunk insert failed`,
        { sql: prettyPrintSQL(query.toSQL()) },
        err instanceof Error ? err : new Error(String(err)),
      );
      throw err;
    }
  }

  /**
   * Builds a dynamic PostgreSQL insert query with support for bulk inserts and conflict handling.
   *
   * @param db             - The database where the data will be inserted.
   * @param table          - The target table where data will be inserted.
   * @param tableColumns   - A mapping of column names to their respective column definitions.
   * @param unnestFragments - SQL fragments representing the data to be inserted using the UNNEST approach.
   * @param options        - Optional settings for handling conflicts and other bulk insert behaviors.
   */
  private buildQuery(
    db: PgDatabase<PgQueryResultHKT, TSchema>,
    table: PgTable,
    tableColumns: Record<string, AnyPgColumn>,
    unnestFragments: SQL[],
    options?: BulkInsertOptions<TSchema>,
  ): PgInsertDynamic<AnyPgInsert> {
    const sub = db
      .$with("bulk_insert_cte", tableColumns)
      .as(sql.join([sql`SELECT `, sql.join(unnestFragments, sql`, `)]));

    const query = db.insert(table).select(db.with(sub).select().from(sub)).$dynamic();

    if (options?.onConflict?.type === "doNothing") {
      return query.onConflictDoNothing(options.onConflict.target ? { target: options.onConflict.target } : undefined);
    }

    if (options?.onConflict?.type === "doUpdate") {
      return query.onConflictDoUpdate({
        target: options.onConflict.target,
        set: options.onConflict.set,
      });
    }

    return query;
  }

  /**
   * Builds SQL fragments and execution parameters for inserting or updating columns in a database.
   *
   * @param {Record<string, AnyPgColumn>} tableColumns - A mapping of column names to their definitions in the table.
   * @param {Record<string, unknown>[]} rows - An array of row objects containing the data to be processed.
   * @returns {{ unnestFragments: SQL[]; executeParams: Record<string, string> }} An object containing:
   *         - `unnestFragments`: An array of SQL fragments used for column unnesting.
   *         - `executeParams`: A mapping of parameter keys to their corresponding values for SQL execution.
   */
  private static buildColumns(
    tableColumns: Record<string, AnyPgColumn>,
    rows: Record<string, unknown>[],
  ): { unnestFragments: SQL[]; executeParams: Record<string, string> } {
    const presentKeys = new Set<string>();
    for (const row of rows) {
      for (const key of Object.keys(row)) {
        if (key in tableColumns) presentKeys.add(key);
      }
    }

    const executeParams: Record<string, string> = {};
    const unnestFragments: SQL[] = [];

    for (const [key, column] of Object.entries(tableColumns)) {
      const result = BulkInserter.buildFragment(key, column, presentKeys, rows);
      unnestFragments.push(result.fragment);
      if (result.param) executeParams[result.param[0]] = result.param[1];
    }

    return { unnestFragments, executeParams };
  }

  /**
   * Constructs an SQL fragment based on the given column, key, present keys, and rows.
   *
   * The method handles different scenarios such as the presence of a default value, a default function, or an existing
   * key.
   *
   * @param {string} key - The key associated with the column in the row's data.
   * @param {AnyPgColumn} column - The database column metadata, including its name, type, and default values.
   * @param {Set<string>} presentKeys - The set of keys that are already processed or present.
   * @param {Record<string, unknown>[]} rows - The array of data rows used to populate or define values for the column.
   * @returns {{ fragment: SQL; param?: [string, string] }} An object containing the SQL fragment and an optional
   *         parameter array, which includes the key and serialized values for the column if applicable.
   */
  private static buildFragment(
    key: string,
    column: AnyPgColumn,
    presentKeys: Set<string>,
    rows: Record<string, unknown>[],
  ): { fragment: SQL; param?: [string, string] } {
    const colAlias = sql.raw(`"${column.name}"`);

    // Priority 0:
    // At least one row defines a value for this field (imposing a requirement on all the others to also define it)
    if (presentKeys.has(key)) {
      return {
        fragment: BulkInserter.unnestFragment(key, column, colAlias),
        param: [key, makePgArray(rows.map((row) => BulkInserter.serializeValue(row[key], column)))],
      };
    }

    // Priority 1:
    // No row defines a value for this field, and a code-level defaultFn exists for the column
    if (column.defaultFn !== undefined) {
      return {
        fragment: BulkInserter.unnestFragment(key, column, colAlias),
        param: [key, makePgArray(rows.map(() => BulkInserter.serializeValue(column.defaultFn?.(), column)))],
      };
    }

    // Priority 2:
    // No row defines a value for this field, and the column has a raw SQL default value
    if (column.default !== undefined && is(column.default, SQL)) {
      return { fragment: sql.join([column.default, sql` as `, colAlias]) };
    }

    // Priority 3:
    // No row defines a value for this field, and the column has a standard default value
    if (column.default !== undefined) {
      return {
        fragment: sql.join([
          sql`${column.default}`,
          sql`::`,
          sql.raw(BulkInserter.getQuotedSQLType(column)),
          sql` as `,
          colAlias,
        ]),
      };
    }

    // Priority 4:
    // No row defines a value for this field; assume null
    return { fragment: sql.join([sql`null::`, sql.raw(BulkInserter.getQuotedSQLType(column)), sql` as `, colAlias]) };
  }

  /**
   * Builds the `unnest(…) as "col"` fragment, casting array columns so PostgreSQL keeps the extra dimension.
   */
  private static unnestFragment(key: string, column: AnyPgColumn, colAlias: SQL): SQL {
    const parts = [sql`unnest(${sql.placeholder(key)}::`, sql.raw(BulkInserter.getSqlArrayType(column)), sql`)`];

    if (is(column, PgArray)) {
      parts.push(sql`::`, sql.raw(BulkInserter.getQuotedSQLType(column)));
    }

    parts.push(sql` as `, colAlias);
    return sql.join(parts);
  }

  private static getSqlArrayType(column: AnyPgColumn): string {
    if (is(column, PgArray)) return "text[]";
    return is(column, PgEnumColumn) ? `"${column.getSQLType()}"[]` : `${column.getSQLType()}[]`;
  }

  private static getQuotedSQLType(column: AnyPgColumn): string {
    if (is(column, PgArray) && is(column.baseColumn, PgEnumColumn)) {
      return `"${column.baseColumn.getSQLType()}"[]`;
    }
    if (is(column, PgEnumColumn)) return `"${column.getSQLType()}"`;
    return column.getSQLType();
  }

  private static serializeValue(value: unknown, column: AnyPgColumn): unknown {
    // Coalesce nullish to NULL
    if (value === undefined || value === null) {
      return null;
    }

    // Serialize dates to ISO
    if (value instanceof Date) {
      return value.toISOString();
    }

    // Recurse through arrays and serialize their elements
    if (Array.isArray(value) && is(column, PgArray)) {
      return makePgArray(value.map((v) => BulkInserter.serializeValue(v, column.baseColumn)));
    }

    // Serialize JSON
    if (["json", "jsonb"].includes(column.getSQLType())) {
      return JSON.stringify(value);
    }

    // Pass primitives through unmodified
    return value;
  }
}
